import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import trackActivityService from "../../api/trackActivityService";
import { getToken } from "../../utils/auth";

// Skip activity tracking calls when the user is not authenticated.
// On logout the auth token is cleared before page components unmount, and the
// unmount cleanup fires activityEnd / activityPing / trackActivity. Without
// this guard those calls go out tokenless and 401.
const isLoggedIn = () => !!getToken();

export const trackActivity = createAsyncThunk(
  "trackActivity/fetch",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await trackActivityService.trackActivity(payload);
      return response.data;
    } catch (error) {
      console.error("❌ trackActivity API error:", error);
      console.error("Error response:", error.response?.data);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  { condition: () => isLoggedIn() },
);
export const activityStart = createAsyncThunk(
  "trackActivity/activityStart",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await trackActivityService.activityStart(payload);
      return response.data;
    } catch (error) {
      console.error("❌ activityStart API error:", error);
      console.error("Error response:", error.response?.data);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
export const activityPing = createAsyncThunk(
  "trackActivity/activityPing",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await trackActivityService.activityPing(payload);
      return response.data;
    } catch (error) {
      console.error("❌ activityPing API error:", error);
      console.error("Error response:", error.response?.data);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  { condition: () => isLoggedIn() },
);
export const activityEnd = createAsyncThunk(
  "trackActivity/activityEnd",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await trackActivityService.activityEnd(payload);
      return response.data;
    } catch (error) {
      console.error("❌ activityEnd API error:", error);
      console.error("Error response:", error.response?.data);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  { condition: () => isLoggedIn() },
);
export const activationTrack = createAsyncThunk(
  "trackActivity/activationTrack",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await trackActivityService.activationTrack(payload);
      return response.data;
    } catch (error) {
      console.error("❌ activationTrack API error:", error);
      console.error("Error response:", error.response?.data);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const initialState = {
  trackActivityItems: [],
  activityStartItems: [],
  activityPingItems: [],
  activityEndItems: [],
  activationTrackItems: [],
  total: 0,
  currentPage: 1,
  totalPages: 1,
  perPage: 6,

  isLoading: false,
  isError: false,
  errorMessage: "",
  hasInitialized: false,
};

const trackActivitySlice = createSlice({
  name: "trackActivity",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(trackActivity.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(trackActivity.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasInitialized = true;
        state.isError = false;

        state.trackActivityItems = action.payload?.data || null;
      })
      .addCase(trackActivity.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.hasInitialized = true;
        state.errorMessage =
          action.payload || "Failed to load track activity data.";
      })
      .addCase(activityPing.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(activityPing.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasInitialized = true;
        state.isError = false;

        state.activityPingItems = action.payload?.data || null;
      })
      .addCase(activityPing.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.hasInitialized = true;
        state.errorMessage =
          action.payload || "Failed to load activity  ping data.";
      })
      .addCase(activityEnd.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(activityEnd.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasInitialized = true;
        state.isError = false;

        state.activityEndItems = action.payload?.data || null;
      })
      .addCase(activityEnd.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.hasInitialized = true;
        state.errorMessage =
          action.payload || "Failed to load activity  ping data.";
        // state.lifeInsuranceItems = [];
      })
      .addCase(activationTrack.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(activationTrack.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasInitialized = true;
        state.isError = false;

        state.activationTrackItems = action.payload?.data || null;
      })
      .addCase(activationTrack.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.hasInitialized = true;
        state.errorMessage =
          action.payload || "Failed to load activationTrack data.";
      })
      .addCase(activityStart.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(activityStart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasInitialized = true;
        state.isError = false;

        state.activityStartItems = action.payload?.data || null;
      })
      .addCase(activityStart.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.hasInitialized = true;
        state.errorMessage =
          action.payload || "Failed to load activity start data.";
        // state.lifeInsuranceItems = [];
      });
  },
});

export default trackActivitySlice.reducer;
